/**
 * CSV Export functionality for Dashboard
 * Exports form submission data filtered by date range
 */
(function () {
  "use strict";

  /**
   * Format date from YYYY-MM-DD to DD-MM-YYYY
   */
  function formatDate(dateStr) {
    if (!dateStr) return "";

    // Parse YYYY-MM-DD format
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      return `${day}-${month}-${year}`;
    }

    return dateStr;
  }

  /**
   * Convert data to CSV format
   */
  function convertToCSV(data, fieldLabels, trackedFields) {
    if (!data || data.length === 0) {
      return "";
    }

    // Build header row - Date, Ward, then all tracked questions
    const headers = ["Submission Date", "Ward"];

    // Add tracked field headers using human-readable labels
    trackedFields.forEach((fieldName) => {
      const label = fieldLabels[fieldName] || fieldName;
      headers.push(label);
    });

    // Build CSV rows
    const rows = [headers];

    data.forEach((row) => {
      // Format the date as dd-mm-yyyy
      const formattedDate = formatDate(row.submission_date);

      const csvRow = [formattedDate || "", row.ward_name || ""];

      // Add tracked field values
      trackedFields.forEach((fieldName) => {
        const value = row[fieldName] !== undefined ? row[fieldName] : "";
        csvRow.push(value);
      });

      rows.push(csvRow);
    });

    // Convert to CSV string
    return rows
      .map((row) => {
        return row
          .map((cell) => {
            // Escape cells that contain commas, quotes, or newlines
            const cellStr = String(cell);
            if (
              cellStr.includes(",") ||
              cellStr.includes('"') ||
              cellStr.includes("\n")
            ) {
              return '"' + cellStr.replace(/"/g, '""') + '"';
            }
            return cellStr;
          })
          .join(",");
      })
      .join("\n");
  }

  /**
   * Download CSV file
   */
  function downloadCSV(csvContent, filename) {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, filename);
    } else {
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Get current filter values from the dashboard
   */
  function getCurrentFilters() {
    const startDateInput = document.getElementById("startMonth");
    const endDateInput = document.getElementById("endMonth");

    const startDate = startDateInput
      ? startDateInput.getAttribute("data-value")
      : null;
    const endDate = endDateInput
      ? endDateInput.getAttribute("data-value")
      : null;

    return {
      startDate: startDate,
      endDate: endDate,
    };
  }

  /**
   * Build API URL with date filters
   */
  function buildApiUrl(startDate, endDate) {
    // Get the base URL from WordPress localized data if available
    const baseUrl =
      window.mchriCsvExport && window.mchriCsvExport.restUrl
        ? window.mchriCsvExport.restUrl + "mchri/v1/form-submissions/csv-export"
        : window.location.origin +
          "/wp-json/mchri/v1/form-submissions/csv-export";

    const params = new URLSearchParams();

    if (startDate) {
      params.append("start_date", startDate);
    }
    if (endDate) {
      params.append("end_date", endDate);
    }

    const queryString = params.toString();
    return queryString ? baseUrl + "?" + queryString : baseUrl;
  }

  /**
   * Generate filename with hospital name and actual submission date range
   */
  function generateFilename(hospitalName, actualStartDate, actualEndDate) {
    // Sanitize hospital name for filename (remove special characters)
    let sanitizedHospitalName = hospitalName || "export";
    sanitizedHospitalName = sanitizedHospitalName
      .replace(/[^a-zA-Z0-9\s-]/g, "") // Remove special chars
      .replace(/\s+/g, "_") // Replace spaces with underscores
      .toLowerCase();

    let filename = sanitizedHospitalName;

    // Format dates from YYYY-MM-DD to DD-MM-YYYY for filename
    if (actualStartDate && actualEndDate) {
      const formattedStart = formatDate(actualStartDate);
      const formattedEnd = formatDate(actualEndDate);
      filename += `_${formattedStart}_to_${formattedEnd}`;
    } else if (actualStartDate) {
      const formattedStart = formatDate(actualStartDate);
      filename += `_from_${formattedStart}`;
    } else if (actualEndDate) {
      const formattedEnd = formatDate(actualEndDate);
      filename += `_until_${formattedEnd}`;
    } else {
      // Fallback to current date if no submission dates
      const now = new Date();
      const day = String(now.getDate()).padStart(2, "0");
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const year = now.getFullYear();
      filename += `_${day}-${month}-${year}`;
    }

    filename += ".csv";
    return filename;
  }

  /**
   * Show loading state on button
   */
  function setButtonLoading(button, isLoading) {
    if (isLoading) {
      button.disabled = true;
      button.dataset.originalText = button.innerHTML;
      button.innerHTML =
        '<svg width="22px" height="22px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="animation: spin 1s linear infinite;"><circle cx="12" cy="12" r="10" stroke="#ffffff" stroke-width="4" fill="none" opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke="#ffffff" stroke-width="4" fill="none" stroke-linecap="round"/></svg> Exporting...';
    } else {
      button.disabled = false;
      if (button.dataset.originalText) {
        button.innerHTML = button.dataset.originalText;
        delete button.dataset.originalText;
      }
    }
  }

  /**
   * Handle export button click
   */
  async function handleExportClick(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const filters = getCurrentFilters();

    try {
      setButtonLoading(button, true);

      // Build API URL
      const apiUrl = buildApiUrl(filters.startDate, filters.endDate);

      // Build headers with nonce for authentication
      const headers = {
        "Content-Type": "application/json",
      };

      // Add nonce if available
      if (window.mchriCsvExport && window.mchriCsvExport.nonce) {
        headers["X-WP-Nonce"] = window.mchriCsvExport.nonce;
      }

      // Fetch data from API
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: headers,
        credentials: "same-origin", // Include cookies for authentication
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error("API returned unsuccessful response");
      }

      if (!result.data || result.data.length === 0) {
        alert("No data available for export with the current filters.");
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(
        result.data,
        result.field_labels,
        result.tracked_fields
      );

      if (!csvContent) {
        throw new Error("Failed to generate CSV content");
      }

      // Generate filename using hospital name and actual submission dates
      const hospitalName = result.hospital_name || "export";
      const actualStartDate = result.actual_date_range
        ? result.actual_date_range.start_date
        : null;
      const actualEndDate = result.actual_date_range
        ? result.actual_date_range.end_date
        : null;
      const filename = generateFilename(
        hospitalName,
        actualStartDate,
        actualEndDate
      );

      // Download CSV
      downloadCSV(csvContent, filename);

      // Reset button state first
      setButtonLoading(button, false);

      // Show success message temporarily
      const originalText = button.dataset.originalText || button.innerHTML;
      button.innerHTML =
        '<svg width="22px" height="22px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#ffffff" stroke-width="2" fill="none"/></svg> Exported!';

      setTimeout(() => {
        button.innerHTML = originalText;
        button.disabled = false;
      }, 2000);
    } catch (error) {
      console.error("[CSV Export] Error:", error);
      alert("Failed to export CSV: " + error.message);
      setButtonLoading(button, false);
    }
  }

  /**
   * Initialize export button
   */
  function init() {
    const exportButton = document.getElementById("exportCSVButton");

    if (!exportButton) {
      return;
    }

    // Check if already initialized to prevent duplicate listeners
    if (exportButton.dataset.csvExportInitialized === "true") {
      return;
    }

    // Mark as initialized
    exportButton.dataset.csvExportInitialized = "true";

    // Add click event listener
    exportButton.addEventListener("click", handleExportClick);

    // Add CSS for spinning animation (only if not already added)
    if (!document.getElementById("csv-export-styles")) {
      const style = document.createElement("style");
      style.id = "csv-export-styles";
      style.textContent = `
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `;
      document.head.appendChild(style);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
